import React, { useState } from 'react';
import { Edit2, Save, X } from 'lucide-react';

/**
 * InvoiceNumberField Component
 * 
 * A reusable component for displaying and editing invoice numbers.
 * By default, the invoice number is read-only with an Edit button.
 * When editing, Save and Cancel buttons are displayed.
 * 
 * @param {string} value - Current invoice number value
 * @param {function} onChange - Callback when the value changes (called on Save)
 * @param {string} label - Label for the field (default: "Invoice Number")
 * @param {string} placeholder - Placeholder text (default: "Generating...")
 */
const InvoiceNumberField = ({
    value = '',
    onChange,
    label = 'Invoice Number',
    placeholder = 'Generating...'
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [tempValue, setTempValue] = useState(value);

    // Update temp value when prop value changes (e.g., auto-generation)
    React.useEffect(() => {
        if (!isEditing) {
            setTempValue(value);
        }
    }, [value, isEditing]);

    const handleEdit = () => {
        setTempValue(value);
        setIsEditing(true);
    };

    const handleSave = () => {
        if (onChange) {
            onChange(tempValue);
        }
        setIsEditing(false);
    };

    const handleCancel = () => {
        setTempValue(value);
        setIsEditing(false);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSave();
        } else if (e.key === 'Escape') {
            e.preventDefault();
            handleCancel();
        }
    };

    return (
        <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
                {label}
            </label>

            {isEditing ? (
                // Edit Mode
                <div className="flex items-center space-x-2">
                    <input
                        type="text"
                        value={tempValue}
                        onChange={(e) => setTempValue(e.target.value)}
                        onKeyDown={handleKeyDown}
                        className="flex-1 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                        placeholder={placeholder}
                        autoFocus
                    />
                    <button
                        type="button"
                        onClick={handleSave}
                        className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-1"
                        title="Save"
                    >
                        <Save className="h-4 w-4" />
                        <span className="text-sm">Save</span>
                    </button>
                    <button
                        type="button"
                        onClick={handleCancel}
                        className="px-3 py-2 bg-slate-500 text-white rounded-lg hover:bg-slate-600 transition-colors flex items-center space-x-1"
                        title="Cancel"
                    >
                        <X className="h-4 w-4" />
                        <span className="text-sm">Cancel</span>
                    </button>
                </div>
            ) : (
                // Read-Only Mode
                <div className="flex items-center space-x-2">
                    <div className="flex-1 px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-sm text-slate-900">
                        {value || <span className="text-slate-400">{placeholder}</span>}
                    </div>
                    <button
                        type="button"
                        onClick={handleEdit}
                        className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-1"
                        title="Edit Invoice Number"
                    >
                        <Edit2 className="h-4 w-4" />
                        <span className="text-sm">Edit</span>
                    </button>
                </div>
            )}

            {!isEditing && (
                <p className="text-xs text-slate-500 mt-1">
                    Auto-generated. Click Edit to modify if needed.
                </p>
            )}
        </div>
    );
};

export default InvoiceNumberField;
